import React, { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../Logo";
import AuthButton from "../AuthButton";
import SignupButton from "../auth/SignupButton";
import HeaderNavigation from "./HeaderNavigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/auth";
import NotificationBell from "@/components/domains/notifications/NotificationBell";
import SearchBar from "@/components/SearchBar";
import { Linkedin, Instagram, Search, X, Menu, ArrowLeft, Save, Eye, ChevronDown, History, RotateCcw, Plus, BookOpen, Target, Settings, Users, BarChart3, Type, AlertTriangle, Lightbulb, Flag, RefreshCw, Scale, TrendingUp, Image, Quote, Grid3X3, Images, Minus, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SaveStatusIndicator } from "@/components/dashboard/profile/portfolio/SaveStatusIndicator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CASE_STUDY_SECTIONS, CASE_STUDY_TEMPLATES, ContentBlockType } from "@/types/case-study";

interface CustomLayoutProps {
  children: ReactNode;
  // Case study editor props
  caseStudyMode?: boolean;
  caseStudy?: any;
  // Shot editor props
  shotMode?: boolean;
  shot?: any;
  // Common editor props
  isSaving?: boolean;
  isTitleValid?: boolean;
  saveStatus?: any;
  showAutosaveIndicator?: boolean;
  onCancel?: () => void;
  onSave?: (isDraft: boolean) => void;
  onPreview?: () => void;
  onAddSection?: (sectionType: string) => void;
  onAddContentBlock?: (blockType: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onSaveVersion?: () => void;
  onShowVersionHistory?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  versionHistoryItems?: VersionHistoryItem[];
  versionHistoryDescription?: string;
  // Shot-specific props
  isDuplicating?: boolean;
  savingAsDraft?: boolean;
  canSaveDraft?: boolean;
  canPreview?: boolean;
  canPublish?: boolean;
}

interface VersionHistoryItem {
  id: string;
  title: string;
  timestamp?: string;
  onRestore?: () => void;
}

// Map icon names to Lucide components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen,
  Target,
  Settings,
  Users,
  BarChart3,
  Type,
  AlertTriangle,
  Lightbulb,
  RotateCcw,
  Flag,
  RefreshCw,
  Scale,
  TrendingUp,
  Image,
  Quote,
  Grid3X3,
  Images,
  Minus,
  Info,
};

// Content block types for Elements tab
const CONTENT_BLOCKS = [
  {
    type: "text" as ContentBlockType,
    title: "Text Section",
    description: "Add rich text content",
    icon: "Type",
    color: "bg-purple-100 text-purple-600",
  },
  {
    type: "image" as ContentBlockType,
    title: "Image, Video, GIF",
    description: "Upload media files",
    icon: "Image",
    color: "bg-green-100 text-green-600",
  },
  {
    type: "gallery" as ContentBlockType,
    title: "Image Gallery",
    description: "Create image galleries",
    icon: "Grid3X3",
    color: "bg-blue-100 text-blue-600",
  },
  {
    type: "carousel" as ContentBlockType,
    title: "Carousel",
    description: "Create image carousels",
    icon: "Images",
    color: "bg-indigo-100 text-indigo-600",
  },
  {
    type: "line-break" as ContentBlockType,
    title: "Line Break",
    description: "Add spacing",
    icon: "Minus",
    color: "bg-gray-100 text-gray-600",
  },
  {
    type: "quote" as ContentBlockType,
    title: "Quote Block",
    description: "Add styled quotes",
    icon: "Quote",
    color: "bg-orange-100 text-orange-600",
  },
  {
    type: "callout" as ContentBlockType,
    title: "Callout",
    description: "Add informational callouts",
    icon: "Info",
    color: "bg-blue-100 text-blue-600",
  },
];

const CustomLayout: React.FC<CustomLayoutProps> = ({
  children,
  caseStudyMode = false,
  caseStudy,
  shotMode = false,
  shot,
  isSaving = false,
  isTitleValid = true,
  saveStatus,
  showAutosaveIndicator = false,
  onCancel,
  onSave,
  onPreview,
  onAddSection,
  onAddContentBlock,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onSaveVersion,
  onShowVersionHistory,
  onDuplicate,
  onDelete,
  versionHistoryItems = [],
  versionHistoryDescription = "Review changes, revert to a version.",
  isDuplicating = false,
  savingAsDraft = false,
  canSaveDraft = true,
  canPreview = true,
  canPublish = true,
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [communityExpanded, setCommunityExpanded] = useState(false);

  const isProfilePage = location.pathname.startsWith('/profile') || location.pathname.startsWith('/u/');
  const isDashboardPage = location.pathname.includes("/dashboard");
  const isSettingsOrNotificationsPage =
    location.pathname.includes("/settings") ||
    location.pathname.includes("/notifications");
  const isCaseStudyEditorPage = location.pathname.includes("/portfolio/case-study") || location.pathname.includes("/portfolio/shot");

  const showFooter = !isDashboardPage && !isSettingsOrNotificationsPage;

  return (
    <div className="min-h-screen w-full bg-transparent animate-fade-in flex flex-col">
      {/* Fixed header positioned below top bar */}
      <header className={`fixed left-0 right-0 w-full bg-white border-b border-gray-200 z-40 ${
        isCaseStudyEditorPage ? 'top-0' : 'top-[50px]'
      }`}>
        <div className={`container px-3 sm:px-4 flex justify-between items-center ${
          !caseStudyMode && !shotMode ? 'py-5' : ''
        }`}>
          {/* Case Study Mode Header */}
          {caseStudyMode && caseStudy ? (
            <>
              <div className="flex items-center gap-4">
                <Logo variant="circular" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={onCancel} className="bg-gray-100 border-none">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>

                  <div className="hidden md:flex items-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                        >
                          <span className="max-w-[260px] truncate">
                            {caseStudy.title
                              ? caseStudy.title.length > 50
                                ? caseStudy.title.substring(0, 50) + '...'
                                : caseStudy.title
                              : 'Untitled Case Study'}
                          </span>
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        sideOffset={8}
                        className="w-64 rounded-xl border border-gray-200 shadow-xl"
                      >
                        <DropdownMenuItem
                          onSelect={() => onUndo?.()}
                          disabled={!canUndo}
                          className="flex items-center text-gray-700"
                        >
                          <span>Undo</span>
                          <DropdownMenuShortcut>⌘ Z</DropdownMenuShortcut>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => onRedo?.()}
                          disabled={!canRedo}
                          className="flex items-center text-gray-700"
                        >
                          <span>Redo</span>
                          <DropdownMenuShortcut>⇧ ⌘ Z</DropdownMenuShortcut>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="w-full rounded-md px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 data-[state=open]:bg-gray-100 focus:bg-gray-100">
                            <span>Version History</span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent
                            sideOffset={12}
                            className="w-72 rounded-2xl border border-gray-200 bg-white p-0 shadow-xl"
                          >
                            <div className="space-y-1 border-b border-gray-100 px-4 py-3">
                              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                <History className="h-4 w-4 text-gray-500" />
                                <span>Version History</span>
                              </div>
                              {versionHistoryDescription && (
                                <p className="text-xs text-gray-500">
                                  {versionHistoryDescription}
                                </p>
                              )}
                            </div>
                            <div className="max-h-64 overflow-auto py-1">
                              {versionHistoryItems.length > 0 ? (
                                versionHistoryItems.map((item) => (
                                  <DropdownMenuItem
                                    key={item.id}
                                    onSelect={() => {
                                      item.onRestore?.();
                                      onShowVersionHistory?.();
                                    }}
                                    className="flex items-start gap-3 py-3 text-gray-700 hover:bg-gray-50 focus:bg-gray-50"
                                  >
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900">
                                        {item.title}
                                      </p>
                                      {item.timestamp && (
                                        <p className="mt-0.5 text-xs text-gray-500">
                                          {item.timestamp}
                                        </p>
                                      )}
                                    </div>
                                    <RotateCcw className="mt-0.5 h-4 w-4 text-gray-400" />
                                  </DropdownMenuItem>
                                ))
                              ) : (
                                <div className="px-4 py-4 text-sm text-gray-500">
                                  No version history yet.
                                </div>
                              )}
                            </div>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuItem
                          onSelect={() => onSaveVersion?.()}
                          className="text-gray-700"
                        >
                          Save Version...
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => onDuplicate?.()}
                          className="text-gray-700"
                        >
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => onDelete?.()}
                          className="text-red-600 focus:text-red-600"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center gap-1 rounded-full py-0.5 text-xs ${
                        caseStudy.status === 'published'
                          ? 'text-[#6B7280]'
                          : 'text-[#6B7280]'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          caseStudy.status === 'published'
                            ? 'bg-green-500'
                            : 'bg-[#EAB308]'
                        }`} />
                        {caseStudy.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      {/* Autosave status indicator */}
                      {showAutosaveIndicator && saveStatus && (
                        <SaveStatusIndicator status={saveStatus} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-4 p-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isSaving}
                        className="border-none "
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Section
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-3 shadow-xl border border-gray-200 rounded-2xl" align="start">
                      <Tabs defaultValue="sections" className="w-full">
                        <div className="bg-white p-2 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.05),inset_0_-2px_4px_rgba(0,0,0,0.05),inset_2px_0_4px_rgba(0,0,0,0.05),inset_-2px_0_4px_rgba(0,0,0,0.05)]">
                          <TabsList className="h-auto bg-transparent p-0 justify-start w-full">
                            <div className="flex items-center gap-1 w-full">
                              <TabsTrigger
                                value="sections"
                                className="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-md data-[state=active]:border data-[state=active]:border-gray-200 data-[state=inactive]:text-[#737373] data-[state=inactive]:font-medium data-[state=inactive]:bg-transparent"
                              >
                                Sections
                              </TabsTrigger>
                              <TabsTrigger
                                value="elements"
                                className="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-md data-[state=active]:border data-[state=active]:border-gray-200 data-[state=inactive]:text-[#737373] data-[state=inactive]:font-medium data-[state=inactive]:bg-transparent"
                              >
                                Elements
                              </TabsTrigger>
                            </div>
                          </TabsList>
                        </div>

                        <TabsContent value="sections" className="mt-0 pt-3">
                          <div className="relative">
                            <div className="grid gap-1 max-h-[380px] overflow-y-auto">
                              {CASE_STUDY_SECTIONS.map((section) => {
                                const matchingTemplate = CASE_STUDY_TEMPLATES.find(
                                  (template) => template.type === section.type
                                );
                                const iconName = matchingTemplate?.icon ?? section.icon;
                                const Icon = iconMap[iconName];
                                return (
                                  <button
                                    key={section.type}
                                    onClick={() => onAddSection?.(section.type)}
                                    className="flex items-start gap-3 rounded-lg p-3 text-left hover:bg-gray-100 transition-colors"
                                  >
                                    {Icon && <Icon className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900">{section.title}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-2xl" />
                          </div>
                        </TabsContent>

                        <TabsContent value="elements" className="mt-0 pt-3">
                          <div className="relative">
                            <div className="grid gap-1 max-h-[380px] overflow-y-auto">
                              {CONTENT_BLOCKS.map((block) => {
                                const Icon = iconMap[block.icon];
                                return (
                                  <button
                                    key={block.type}
                                    onClick={() => onAddContentBlock?.(block.type)}
                                    className="flex items-start gap-3 rounded-lg p-3 text-left hover:bg-gray-100 transition-colors"
                                  >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${block.color}`}>
                                      {Icon && <Icon className="h-5 w-5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900">{block.title}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{block.description}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-2xl" />
                          </div>
                        </TabsContent>
                      </Tabs>
                    </PopoverContent>
                  </Popover>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onPreview}
                    disabled={isSaving || !isTitleValid}
                    className="bg-gray-100 border-none"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onSave?.(false)}
                    disabled={isSaving || !isTitleValid}
                  >
                    {isSaving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                    ) : (
                      <img src="/publish-white.svg" alt="" className="h-4 w-4 mr-1" />
                    )}
                    {isSaving
                      ? (caseStudy.status === 'published' ? 'Updating...' : 'Publishing...')
                      : (caseStudy.status === 'published' ? 'Update' : 'Publish')
                    }
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  {user && <NotificationBell />}
                  {!isMobile && <AuthButton />}
                  {isMobile && <SignupButton />}
                </div>
              </div>
            </>
          ) : shotMode && shot ? (
            /* Shot Mode Header */
            <>
              <div className="flex items-center gap-4">
                <Logo variant="circular" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={onCancel} className="bg-gray-100 border-none">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>

                  <div className="hidden md:flex items-center gap-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900/10 rounded-md"
                        >
                          <span className="max-w-[200px] truncate">
                            {shot.title
                              ? shot.title.length > 40
                                ? shot.title.substring(0, 40) + '...'
                                : shot.title
                              : 'Untitled Shot'}
                          </span>
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        sideOffset={8}
                        className="w-64 rounded-xl border border-gray-200 shadow-xl"
                      >
                        <DropdownMenuItem
                          onSelect={() => onUndo?.()}
                          disabled={!canUndo}
                          className="flex items-center text-gray-700"
                        >
                          <span>Undo</span>
                          <DropdownMenuShortcut>⌘ Z</DropdownMenuShortcut>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => onRedo?.()}
                          disabled={!canRedo}
                          className="flex items-center text-gray-700"
                        >
                          <span>Redo</span>
                          <DropdownMenuShortcut>⇧ ⌘ Z</DropdownMenuShortcut>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="w-full rounded-md px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 data-[state=open]:bg-gray-100 focus:bg-gray-100">
                            <span>Version History</span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent
                            sideOffset={12}
                            className="w-72 rounded-2xl border border-gray-200 bg-white p-0 shadow-xl"
                          >
                            <div className="space-y-1 border-b border-gray-100 px-4 py-3">
                              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                <History className="h-4 w-4 text-gray-500" />
                                <span>Version History</span>
                              </div>
                              <p className="text-xs text-gray-500">
                                Review changes, revert to a version.
                              </p>
                            </div>
                            <div className="max-h-64 overflow-auto py-1">
                              {versionHistoryItems.length > 0 ? (
                                versionHistoryItems.map((item) => (
                                  <DropdownMenuItem
                                    key={item.id}
                                    onSelect={() => item.onRestore?.()}
                                    className="flex items-start gap-3 py-3 text-gray-700 hover:bg-gray-50 focus:bg-gray-50"
                                  >
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900">
                                        {item.title}
                                      </p>
                                      {item.timestamp && (
                                        <p className="mt-0.5 text-xs text-gray-500">
                                          {item.timestamp}
                                        </p>
                                      )}
                                    </div>
                                    <RotateCcw className="mt-0.5 h-4 w-4 text-gray-400" />
                                  </DropdownMenuItem>
                                ))
                              ) : (
                                <div className="px-4 py-4 text-sm text-gray-500">
                                  No version history yet.
                                </div>
                              )}
                            </div>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuItem
                          onSelect={() => onSaveVersion?.()}
                          className="text-gray-700"
                        >
                          Save Version...
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => onDuplicate?.()}
                          disabled={isDuplicating}
                          className="text-gray-700"
                        >
                          <span>{isDuplicating ? 'Duplicating...' : 'Duplicate'}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => onDelete?.()}
                          className="text-red-600 focus:text-red-600"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center gap-1 rounded-full py-0.5 text-xs ${
                        shot.status === 'published'
                          ? 'text-[#6B7280]'
                          : 'text-[#6B7280]'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          shot.status === 'published'
                            ? 'bg-green-500'
                            : 'bg-[#EAB308]'
                        }`} />
                        {shot.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      {/* Autosave status indicator */}
                      {showAutosaveIndicator && saveStatus && (
                        <SaveStatusIndicator status={saveStatus} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-4 p-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onPreview}
                    disabled={isSaving || !canPreview}
                    className="bg-gray-100 border-none"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onSave?.(false)}
                    disabled={isSaving || !canPublish}
                    className="hover:bg-black/90 disabled:hover:bg-black"
                  >
                    {isSaving && !savingAsDraft ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                    ) : (
                      <img src="/publish-white.svg" alt="" className="h-4 w-4 mr-1" />
                    )}
                    {isSaving && !savingAsDraft
                      ? (shot.status === 'published' ? 'Updating...' : 'Publishing...')
                      : (shot.status === 'published' ? 'Update' : 'Publish')
                    }
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  {user && <NotificationBell />}
                  {!isMobile && <AuthButton />}
                  {isMobile && <SignupButton />}
                </div>
              </div>
            </>
          ) : (
            /* Normal Mode Header */
            <>
              <div className="flex items-center">
                <Logo />
              </div>
              <div className="flex items-center space-x-3 sm:space-x-4">
                {user && <NotificationBell />}
                {!isMobile && <AuthButton />}
                {isMobile && <SignupButton />}
                {/* Mobile burger menu button */}
                {isMobile && (
                  <button
                    className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Open mobile menu"
                  >
                    <Menu size={24} />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobile && (
        <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <DialogContent className="h-full flex flex-col justify-end items-center max-w-none bg-white/20 backdrop-blur-md border-none shadow-none">
            <div className="">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
                aria-label="Close mobile menu"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
            
            <div className="relative w-full max-w-[700px] bg-white rounded-xl overflow-hidden flex flex-col h-[88vh] md:h-[90vh]">
              <div className="flex-1 overflow-y-auto px-4 md:px-6">
                <div className="space-y-3 py-2">
                  <div className="space-y-1 py-2">
                    <Link
                      to="/"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Explore
                    </Link>
                    <Link
                      to="/designers"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Designers
                    </Link>
                    <Link
                      to="/"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Home
                    </Link>
                    <Link
                      to="/pricing"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Pricing
                    </Link>
                    
                    {/* Community expandable section */}
                    <div>
                      <button
                        className="flex items-center w-full py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                        onClick={() => setCommunityExpanded(!communityExpanded)}
                      >
                        <span>Community</span>
                        <svg
                          className={`w-4 h-4 ml-2 transition-transform ${communityExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {communityExpanded && (
                        <div className="ml-4 mt-2 space-y-1">
                          <a
                            href="https://chat.whatsapp.com/GxGCQpa8S6l0E3i5dKZGQM?mode=ac_t"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            WCD Global
                          </a>
                          <a
                            href="https://chat.whatsapp.com/FEGXfBQSxSZBYiZqsCeKvP?mode=ac_t"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            WCD Paris
                          </a>
                          <a
                            href="https://chat.whatsapp.com/Gp3jN7g9fNQJg0eOSiw4r8?mode=ac_t"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            WCD Montpellier
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {!user && (
                      <Link
                        to="/auth"
                        className="flex items-center py-3 px-4 text-base font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors border-t border-gray-100 mt-4 pt-4"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Search Modal - Desktop Only */}
      {!isMobile && (
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogContent className="max-w-2xl w-full bg-transparent border-none shadow-none p-0">
            <div className="w-full max-w-2xl mx-auto -mt-72">
              <SearchBar />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* No padding needed since header is not fixed */}
      <main className="flex-grow relative">{children}</main>

      {showFooter && !isMobile && (
        <footer className="relative overflow-hidden bg-black text-white py-9 z-40 flex items-center justify-center bg-[url('/footer-bg.png')] bg-cover bg-center bg-no-repeat">
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0" />
          <div className="container mx-auto px-4 w-full relative z-10">
            <div className="flex flex-col justify-between w-full gap-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-8">
                  <div className="text-white/60 text-sm">
                    World Class Design © {new Date().getFullYear()}
                  </div>

                  <div className="flex items-center">
                    <div className="p-3">
                      <a
                        href="https://www.linkedin.com/company/africans-who-design/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <Linkedin size={18} />
                      </a>
                    </div>

                    <div className="p-3">
                      <a
                        href="https://www.instagram.com/africanswhodesign.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <Instagram size={18} />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <Link
                    to="/privacy"
                    className="text-white hover:text-gray-300 transition text-sm"
                  >
                    Privacy Policy
                  </Link>
                  
                  <span className="mx-2 text-white text-lg">|</span>
                  
                  <Link
                    to="/terms"
                    className="text-white hover:text-gray-300 transition text-sm"
                  >
                    Terms of Service
                  </Link>

                  <span className="mx-2 text-white text-lg">|</span>

                  <a
                    href="https://substack.com/@africanswhodesign"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-gray-300 transition text-sm"
                  >
                    What is New
                  </a>
                  
                  <span className="mx-2 text-white text-lg">|</span>

                  <Link
                    to="/learn-more"
                    className="text-white hover:text-gray-300 transition text-sm"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default CustomLayout;
